import { Expense } from './expenses';

export interface NoteColor {
  bg: string;
  edge: string;
  ink: string;
}

export const PALETTE: NoteColor[] = [
  { bg: '#fff3c2', edge: '#f3d97a', ink: '#5b4d1e' }, // yellow
  { bg: '#ffdfea', edge: '#f6a8c4', ink: '#6b2b45' }, // pink
  { bg: '#d8f5e2', edge: '#8fd9ae', ink: '#1f5238' }, // mint
  { bg: '#e3e0ff', edge: '#a99df0', ink: '#3a3280' }, // lavender
  { bg: '#d9efff', edge: '#8fc8f0', ink: '#1e4a6b' }, // sky
  { bg: '#ffe6d3', edge: '#f3b27e', ink: '#6b3a1e' }, // peach
];

/** Hex backgrounds of the presets — used to tell a preset apart from a custom color. */
export const PALETTE_HEX: string[] = PALETTE.map((c) => c.bg);

function hashString(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Build a full NoteColor (bg + derived edge/ink) from an arbitrary hex color.
 * `edge` is a darker shade of the color, `ink` a much darker one for text.
 * Returns null for invalid input.
 */
export function colorFromHex(hex: string): NoteColor | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return {
    bg: `#${hex.trim().replace(/^#/, '').toLowerCase()}`,
    edge: `rgb(${Math.round(r * 0.72)}, ${Math.round(g * 0.72)}, ${Math.round(b * 0.72)})`,
    ink: `rgb(${Math.round(r * 0.34)}, ${Math.round(g * 0.34)}, ${Math.round(b * 0.34)})`,
  };
}

/**
 * Resolve the note's colors:
 * - hex string → custom color (edge/ink derived from it)
 * - number → legacy stored PALETTE index
 * - undefined → stable hash-based color so the note keeps its color across reorders
 */
export function colorFor(item: Expense): NoteColor {
  const c = item.color;
  if (typeof c === 'number') {
    return PALETTE[c] ?? PALETTE[hashString(item.id) % PALETTE.length];
  }
  if (typeof c === 'string') {
    return colorFromHex(c) ?? PALETTE[hashString(item.id) % PALETTE.length];
  }
  return PALETTE[hashString(item.id) % PALETTE.length];
}
